/**
 * Recent-activity timeline (prototype, GDN-2579): the org's — or one user's —
 * event stream from the ClickHouse analytics store, newest first. Shared by
 * the Activity tabs on the admin organization and user pages: pass `userId`
 * to narrow the timeline to events by (or admin actions on) that user.
 *
 * The lens toggle slices by the envelope's structural flags — Domain
 * (business events), Audit (resource view/list/lifecycle exhaust), Usage
 * (product telemetry) — defaulting to Domain, the "what actually happened"
 * view. On the user page the Actor column is dropped: the actor IS the user
 * on >99% of rows, so only a foreign actor (system pipeline, admin acting
 * on the member) is called out, inline.
 *
 * Rows with a resource registered in the Resources catalog link into the
 * org's Resource Explorer; actor ids resolve to member names via the
 * caller-supplied `userNames` map (the store carries ids only, by design).
 *
 * Rows expand to the event payload, fetched lazily per event. Degrades to
 * nothing when the backend has no analytics store configured.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { RecentActivityEntry } from '../store/api';
import {
  useGetRecentActivityQuery,
  useLazyGetRecentActivityQuery,
  useListEventTagsQuery,
  useListResourceTypesQuery,
} from '../store/hooks';
import { CardTitle } from './card-title';

type ActivityCategory = 'all' | 'domain' | 'audit' | 'usage';

const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const shortId = (id: string): string => (id.length > 12 ? `${id.slice(0, 12)}…` : id);

const explorerUrl = (organizationId: string, type: string, id: string): string =>
  `/admin-v2/organization/${organizationId}?tab=resource-explorer&rtype=${encodeURIComponent(
    type,
  )}&rid=${encodeURIComponent(id)}`;

/** The actor worth calling out on a user timeline: someone other than the member. */
const foreignActor = (
  entry: RecentActivityEntry,
  userId: string | undefined,
  userNames: Record<string, string> | undefined,
): string | null => {
  if (!userId) return null;
  if (entry.actor_kind === 'system') return 'system';
  if (entry.actor_id && entry.actor_id !== userId) {
    return userNames?.[entry.actor_id] ?? shortId(entry.actor_id);
  }
  return null;
};

const ActivityRow = ({
  entry,
  organizationId,
  userId,
  userNames,
  linkableTypes,
}: {
  entry: RecentActivityEntry;
  organizationId: string;
  userId?: string;
  userNames?: Record<string, string>;
  linkableTypes: Set<string>;
}) => {
  const [open, setOpen] = useState(false);
  // Payloads are fetched lazily per event: the list query omits them (the
  // JSON column is the expensive part; one event is cheap). event_id is
  // exact, so no timeline narrowing params are needed here.
  const { data: detail, isFetching: detailLoading } = useGetRecentActivityQuery(
    { organizationId, eventId: entry.event_id, limit: 1 },
    { skip: !open },
  );
  const payload = detail?.entries?.[0]?.data;
  const other = foreignActor(entry, userId, userNames);
  const columns = userId ? 4 : 5;
  const actorId = entry.actor_id ?? entry.user_id ?? null;
  const actorName = actorId ? userNames?.[actorId] : undefined;
  const resourceLinked =
    !!entry.subject_type && !!entry.resource_id && linkableTypes.has(entry.subject_type);

  return (
    <Fragment>
      <TableRow hover onClick={() => setOpen(previous => !previous)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.occurred_at)}</TableCell>
        <TableCell>
          <Chip
            size="xSmall"
            variant="soft"
            color={entry.is_access ? 'default' : 'info'}
            label={entry.event_type}
          />
          {other && (
            <Chip
              size="xSmall"
              variant="soft"
              color="warning"
              label={other === 'system' ? '⚙ system' : `by ${other}`}
              sx={{ ml: 0.75 }}
            />
          )}
        </TableCell>
        <TableCell>
          {entry.subject_type || entry.resource_id ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              {entry.subject_type &&
                (resourceLinked ? (
                  <Link
                    component={RouterLink}
                    to={explorerUrl(organizationId, entry.subject_type, entry.resource_id!)}
                    variant="body2"
                    onClick={event => event.stopPropagation()}
                  >
                    {entry.subject_type}
                  </Link>
                ) : (
                  <Typography variant="body2">{entry.subject_type}</Typography>
                ))}
              {entry.resource_id && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {shortId(entry.resource_id)}
                </Typography>
              )}
            </Box>
          ) : (
            '—'
          )}
        </TableCell>
        {!userId && (
          <TableCell>
            {entry.actor_kind === 'system' ? (
              <Typography variant="body2" color="text.secondary">
                ⚙ system
              </Typography>
            ) : actorName ? (
              <Typography variant="body2">{actorName}</Typography>
            ) : (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {actorId ?? '—'}
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell colSpan={columns} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              {[
                ['event_id', entry.event_id],
                ['resource_id', entry.resource_id],
                ['actor', entry.actor_id && `${entry.actor_kind ?? ''} ${entry.actor_id}`.trim()],
                ['correlation_id', entry.correlation_id],
              ]
                .filter(([, value]) => !!value)
                .map(([label, value]) => (
                  <Typography
                    key={label}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontFamily: 'monospace' }}
                  >
                    {label}: {value}
                  </Typography>
                ))}
              <Box sx={{ mb: 1 }} />
              {detailLoading ? (
                <CircularProgress size={18} />
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(payload ?? {}, null, 2)}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

interface RecentActivityCardProps {
  organizationId: string;
  /** Narrow the timeline to one user (events by them or admin actions on them). */
  userId?: string;
  /** Narrow the timeline to one encounter (plus its notes/transcriptions). */
  encounterId?: string;
  /** Member id → display name, for rendering actors as people instead of ids. */
  userNames?: Record<string, string>;
}

const PAGE_SIZE = 50;

interface PageCursor {
  occurredAt: string;
  eventId: string;
}

export const RecentActivityCard = ({
  organizationId,
  userId,
  encounterId,
  userNames,
}: RecentActivityCardProps) => {
  const [category, setCategory] = useState<ActivityCategory>('domain');
  // Catalog tag filter (GDN-2681): orthogonal to the lens — a tag resolves
  // to an event-type list at query time, so retagging in the Event Catalog
  // reshapes this filter retroactively. Hidden while the vocabulary is
  // empty or unavailable (the picker is additive, never load-bearing).
  const [tag, setTag] = useState('');
  const { data: tagData } = useListEventTagsQuery();
  const tags = tagData?.tags ?? [];
  // Keyset pagination: pages accumulate locally; the (occurred_at, event_id)
  // cursor stays stable while new events keep arriving at the top.
  const [fetchPage, { isFetching }] = useLazyGetRecentActivityQuery();
  const [entries, setEntries] = useState<RecentActivityEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<PageCursor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  // Initial-load failure hides the table; a load-more failure must NOT —
  // pages already on screen stay, with an inline retry.
  const [failed, setFailed] = useState(false);
  const [pageFailed, setPageFailed] = useState(false);
  // The lazy hook doesn't cancel prior in-flight requests, so rapid lens
  // switches race: only the response matching the latest request id may
  // touch state, or the last response to LAND would win over the last
  // request MADE.
  const requestId = useRef(0);

  const loadPage = useCallback(
    async (cursor: PageCursor | null) => {
      const id = ++requestId.current;
      try {
        const result = await fetchPage({
          organizationId,
          userId,
          encounterId,
          category,
          ...(tag ? { tag } : {}),
          limit: PAGE_SIZE,
          ...(cursor ? { beforeOccurredAt: cursor.occurredAt, beforeEventId: cursor.eventId } : {}),
        }).unwrap();
        if (id !== requestId.current) return;
        const pageEntries = result.entries ?? [];
        setEntries(previous => {
          if (!cursor) return pageEntries;
          const seen = new Set(previous.map(entry => entry.event_id));
          return [...previous, ...pageEntries.filter(entry => !seen.has(entry.event_id))];
        });
        setNextCursor(
          result.has_more && result.next_before_occurred_at && result.next_before_event_id
            ? {
                occurredAt: result.next_before_occurred_at,
                eventId: result.next_before_event_id,
              }
            : null,
        );
        setFailed(false);
        setPageFailed(false);
        setErrorCode(undefined);
      } catch (error) {
        if (id !== requestId.current) return;
        if (cursor) {
          setPageFailed(true);
        } else {
          setFailed(true);
          setErrorCode((error as { data?: { error_code?: string } } | undefined)?.data?.error_code);
        }
      } finally {
        if (id === requestId.current) setIsLoading(false);
      }
    },
    [fetchPage, organizationId, userId, encounterId, category, tag],
  );

  const reload = useCallback(() => {
    setIsLoading(true);
    setEntries([]);
    setNextCursor(null);
    setPageFailed(false);
    void loadPage(null);
  }, [loadPage]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Registered resource types gate the explorer links: a subject_type the
  // registry doesn't know would deep-link to a dead detail view.
  const { data: catalog } = useListResourceTypesQuery();
  const linkableTypes = useMemo(
    () => new Set((catalog?.items ?? []).map(item => item.resource_type)),
    [catalog],
  );

  // Only a backend WITHOUT an analytics store hides the card; any other
  // failure (store down, query error) must stay visible as a warning.
  if (errorCode === 'activity_store_not_configured') return null;

  return (
    <Card>
      <CardHeader
        disableTypography
        title={<CardTitle>Activity</CardTitle>}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={category}
              onChange={(_, next: ActivityCategory | null) => {
                if (next) setCategory(next);
              }}
              aria-label="Activity lens"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="domain">Domain</ToggleButton>
              <ToggleButton value="audit">Audit</ToggleButton>
              <ToggleButton value="usage">Usage</ToggleButton>
            </ToggleButtonGroup>
            {tags.length > 0 && (
              <TextField
                select
                size="small"
                label="Tag"
                value={tag}
                onChange={event => setTag(event.target.value)}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="">All</MenuItem>
                {tags.map(entry => (
                  <MenuItem key={entry.tag_name} value={entry.tag_name}>
                    {entry.tag_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <IconButton aria-label="Reload activity" onClick={reload} disabled={isFetching}>
              {isFetching && !isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && failed && (
          <Alert severity="warning">
            The analytics store did not answer; activity is temporarily unavailable.
          </Alert>
        )}
        {!isLoading && !failed && !entries.length && (
          <Typography color="text.secondary">No recorded activity.</Typography>
        )}
        {!isLoading && !failed && !!entries.length && (
          <>
            <Table size="small" aria-label="Recent activity">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>When</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Resource</TableCell>
                  {!userId && <TableCell>Actor</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(entry => (
                  <ActivityRow
                    key={entry.event_id}
                    entry={entry}
                    organizationId={organizationId}
                    userId={userId}
                    userNames={userNames}
                    linkableTypes={linkableTypes}
                  />
                ))}
              </TableBody>
            </Table>
            {nextCursor && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1.5,
                }}
              >
                {pageFailed && (
                  <Typography variant="caption" color="warning.main">
                    Loading more failed —
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => void loadPage(nextCursor)}
                  disabled={isFetching}
                  startIcon={isFetching ? <CircularProgress size={14} /> : undefined}
                >
                  {pageFailed ? 'Retry' : 'Load more'}
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
